import React, { useEffect, useState } from 'react';

import { isValidCron } from 'cron-validator';
import { arrayOf, bool, func, shape } from 'prop-types';

import { defaultSchedulers, UTCValues } from 'Utils';
/**
 * @param {Array} resources - resources list
 * @callback onChangeScheduler - a callback when changing the cron expression
 * @returns JSX.Element
 */

export default function SchedulerResource({
  resources,
  showHeader = true,
  onChangeScheduler,
  disabled
}) {
  const [resourcesList, setResourcesList] = useState(resources);

  useEffect(() => {
    resourcesList.forEach((resource) => {
      resource.isCustomScheduler =
        !Object.keys(defaultSchedulers).includes(resource.scheduler) &&
        !disabled;
      setResourcesList([...resourcesList]);
      onChangeScheduler(resourcesList);
      if (!resource.timezone) {
        resource.timezone = '0';
      }
    });
  }, []);

  function handleOpenSchedulerCustomField(resource) {
    resource.isCustomScheduler = !resource.isCustomScheduler;
    if (resource.isCustomScheduler) {
      resource.user_scheduler = '';
      resource.scheduler = '';
    }
    setResourcesList([...resourcesList]);
    onChangeScheduler(resourcesList);
  }

  const handleChangeResourceScheduler = ({ resource, value }) => {
    resource.isCustomScheduler = false;
    resource.user_scheduler = value;
    resource.scheduler = value;
    setResourcesList([...resourcesList]);
    onChangeScheduler(resourcesList);
  };

  function handleSetSchedulerValue(value, resource) {
    resource.user_scheduler = value;
    resource.scheduler = value;
    setResourcesList([...resourcesList]);
    onChangeScheduler(resourcesList);
  }

  function handleSetTimezone(value, resource) {
    resource.timezone = value;
    setResourcesList([...resourcesList]);
    onChangeScheduler(resourcesList);
  }

  return (
    <div className={disabled ? 'disabled' : ''}>
      {showHeader ? (
        <div className="row sdp-p-4 align-items-center">
          <div className="col sdp-d-flex align-items-center">
            <h3 className="sdp-m-0">Resources</h3>
          </div>
          <div className="col sdp-d-flex align-items-center">
            <h3 className="sdp-m-0">Schedule</h3>
          </div>
        </div>
      ) : (
        ''
      )}
      {resourcesList?.map((resource, index) => {
        return (
          <div
            className={`sdp-card sdp-pb-1 ${disabled ? 'disabled' : ''}`}
            key={`${resource.name + index}`}
          >
            <div className="row sdp-p-0 align-items-center">
              <div className="col-3 sdp-d-flex align-items-start flex-column sdp-mb-1">
                <h3 className="sdp-m-0 sdp-truncate">{resource.name}</h3>
                <small className="sdp-small sdp-truncate">
                  {resource.description}
                </small>
              </div>

              <div className="col sdp-d-flex align-items-center justify-content-center">
                <div className="sdp-btn-group w-100">
                  {Object.entries(defaultSchedulers).map(([crontab, label]) => {
                    return (
                      <button
                        key={label}
                        value={crontab}
                        type="button"
                        className="sdp-btn sdp-btn--soft-outline"
                        style={{
                          background:
                            resource.user_scheduler === crontab && !disabled
                              ? '#306bf6'
                              : '#F8FAF9',
                          border: '1px solid rgba(0, 0, 0, 0.1)',
                          color:
                            resource.user_scheduler === crontab && !disabled
                              ? '#ffffff'
                              : '#7A7E85'
                        }}
                        onClick={() =>
                          handleChangeResourceScheduler({
                            resource,
                            value: crontab
                          })
                        }
                        disabled={disabled}
                      >
                        {label}
                      </button>
                    );
                  })}
                  <div
                    style={{ display: 'flex', gap: '1rem', marginLeft: '1rem' }}
                  >
                    <button
                      className="sdp-btn sdp-btn--soft-outline"
                      type="button"
                      style={{
                        background: resource.isCustomScheduler
                          ? '#306bf6'
                          : '#F8FAF9',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        color: resource.isCustomScheduler
                          ? '#ffffff'
                          : '#7A7E85'
                      }}
                      onClick={() => handleOpenSchedulerCustomField(resource)}
                      disabled={disabled}
                    >
                      Custom
                    </button>
                    <select
                      onChange={(event) =>
                        handleSetTimezone(event.target.value, resource)
                      }
                      defaultValue={resource.timezone || '0'}
                      style={{
                        borderRadius: '0.3rem',
                        color: '#7A7E85',
                        borderColor: '#dddddd',
                        backgroundColor: '#F8FAF9',
                        height: '2.5rem',
                        padding: '0 0.5rem',
                        cursor: 'pointer'
                      }}
                      disabled={disabled}
                    >
                      {UTCValues.map((data) => {
                        return (
                          <option value={data.value} key={data.name}>
                            {data.name}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  {resource.isCustomScheduler ? (
                    <div
                      className={`sdp-fieldset sdp-mb-1${
                        resource.user_scheduler &&
                        !isValidCron(resource.user_scheduler)
                          ? 'sdp-fieldset--error'
                          : ''
                      }`}
                    >
                      <input
                        type="text"
                        id={`scheduler-custom-field-${index}`}
                        className="sdp-form-input"
                        value={
                          resource.user_scheduler ? resource.user_scheduler : ''
                        }
                        onChange={(event) =>
                          handleSetSchedulerValue(event.target.value, resource)
                        }
                        placeholder="Input a cron expression"
                        disabled={disabled}
                      />
                      {resource.user_scheduler &&
                      !isValidCron(resource.user_scheduler) ? (
                        <span className="sdp-fc--error">
                          Invalid cron expression!
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

SchedulerResource.propTypes = {
  resources: arrayOf(shape({})),
  showHeader: bool,
  onChangeScheduler: func,
  disabled: bool
};

SchedulerResource.defaultProps = {
  resources: [],
  showHeader: true,
  onChangeScheduler: () => {},
  disabled: false
};
